import React, { useState } from 'react';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';
import { AxiosResponse } from 'axios';

interface DoorDTO {
  id: number;
  name: string;
  roomId: number;
  floorId: number;
  buildingId: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface UpdateDoorResponse {
  status: string;
  statusDescription: string;
  data: null;
}

interface UpdateDoorModuleProps {
  show: boolean;
  onHide: () => void;
  door: DoorDTO;
  onSuccess: () => void;
}

const UpdateDoorModule: React.FC<UpdateDoorModuleProps> = ({ show, onHide, door, onSuccess }) => {
  const [name, setName] = useState<string>(door.name);
  const [description, setDescription] = useState<string | undefined>(door.description);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!name.trim()) {
      setError('Door name is required');
      setIsLoading(false);
      return;
    }

    const payload = {
      id: door.id,
      name: name.trim(),
      description: description?.trim() || '',
      roomId: door.roomId,
      floorId: door.floorId,
      buildingId: door.buildingId,
    };

    try {
      const response: AxiosResponse = await apiDefinitions.updateDoor(payload);
      if (response.status === 200) {
        const data: UpdateDoorResponse = response.data;
        if (data.status === 'S1000' || data.status === 'SUCCESS') {
          showSuccess("Door updated successfully");
          onSuccess();
          onHide();
        } else {
          showError(data.statusDescription || 'Failed to update door');
        }
      } else {
        showError(response.data?.statusDescription || 'Failed to update door');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
      } else if (err.response?.status === 400) {
        showError('Invalid door data. Please check the input.');
      } else if (err.response?.status === 500) {
        showError('Failed to update door due to server error. Please contact support.');
      } else if (err.response?.status === 404) {
        showError('Door not found.');
      } else {
        showError('Failed to update door. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`modal fade ${show ? 'show d-block' : ''}`} tabIndex={-1} style={{ backgroundColor: show ? 'rgba(0,0,0,0.5)' : 'transparent' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Update Door</h5>
            <button type="button" className="btn-close" onClick={onHide} disabled={isLoading}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">Door Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isLoading}
                  placeholder="Enter door name"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  value={description || ''}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isLoading}
                  placeholder="Enter description (optional)"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={onHide} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-secondary" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Door'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateDoorModule;